import { useRef } from 'react';
import type { OdooTask } from '@/types/odoo';
import { usePlanningStore } from '@/stores/planning';
import { getInstallerPillColor } from '@/components/ui/InstallerPicker';

interface InstallerChipsProps {
  task: OdooTask;
  cardBg: string;
  isEasykit: boolean;
  isAnyModeActive: boolean;
  /** Whether installer editing is allowed for THIS card (already accounts for the
   *  global permission AND per-project scope from the host). */
  editable: boolean;
  projectName: string;
}

export function InstallerChips({
  task,
  cardBg,
  isEasykit,
  isAnyModeActive,
  editable,
  projectName,
}: InstallerChipsProps) {
  const userMap = usePlanningStore((s) => s.userMap);
  const openInstallerEditor = usePlanningStore((s) => s.openInstallerEditor);
  const btnRef = useRef<HTMLButtonElement>(null);

  const installerIds = task.x_studio_installateur || [];
  const showEditButton = editable && !isEasykit;

  return (
    <div
      className={`mt-1 ml-3 flex flex-wrap items-center gap-1 ${isAnyModeActive ? 'pointer-events-none' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {installerIds.length === 0 ? (
        <span className="text-[10px] italic text-td-light">Niet toegewezen</span>
      ) : (
        installerIds.map((instId) => (
          <span
            key={instId}
            title={userMap.get(instId) || `#${instId}`}
            className={`inline-block max-w-[10rem] truncate px-2.5 py-1 text-xs font-semibold rounded-full ${getInstallerPillColor(instId, cardBg)}`}
          >
            {userMap.get(instId) || `#${instId}`}
          </span>
        ))
      )}

      {showEditButton && (
        <button
          ref={btnRef}
          onClick={() => {
            if (!btnRef.current) return;
            const rect = btnRef.current.getBoundingClientRect();
            const pad = 8;
            const spaceBelow = window.innerHeight - rect.bottom - pad;
            const spaceAbove = rect.top - pad;
            const pos = spaceBelow >= spaceAbove
              ? { top: rect.bottom + 4, left: rect.left, maxHeight: spaceBelow }
              : { bottom: window.innerHeight - rect.top + 4, left: rect.left, maxHeight: spaceAbove };
            openInstallerEditor(task.id, installerIds, pos, projectName);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-1 rounded text-td-muted hover:text-td-blue hover:bg-white/80 transition-colors"
          aria-label="Installateurs bewerken"
          title="Installateurs bewerken"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      )}
    </div>
  );
}
